import Abilities from "services/permissions/abilities";
import MapApi from "services/map/api";
import get from "lodash.get";
import VisitorCharacteristicsApi from "./api";
import VisitorCharacteristicsUtils from "./utils";
import VisitorCharacteristicsActionTypes from "./types";

class VisitorCharacteristicsActioner {
  static setQualificationIndicatorsFilters(data) {
    return {
      type: VisitorCharacteristicsActionTypes.SET_QUALIFICATION_INDICATORS_FILTERS,
      data,
    };
  }

  static setQualificationIndicators(data) {
    return {
      type: VisitorCharacteristicsActionTypes.SET_QUALIFICATION_INDICATORS,
      data,
    };
  }

  static setQualificationVisitorsFilters(data) {
    return {
      type: VisitorCharacteristicsActionTypes.SET_QUALIFICATION_VISITORS_FILTERS,
      data,
    };
  }

  static setQualificationVisitors(data) {
    return {
      type: VisitorCharacteristicsActionTypes.SET_QUALIFICATION_VISITORS,
      data,
    };
  }

  static setQualificationTimeFilters(data) {
    return {
      type: VisitorCharacteristicsActionTypes.SET_QUALIFICATION_TIME_FILTERS,
      data,
    };
  }

  static setQualificationTime(data) {
    return {
      type: VisitorCharacteristicsActionTypes.SET_QUALIFICATION_TIME,
      data,
    };
  }

  static setQualificationDestinationFilters(data) {
    return {
      type: VisitorCharacteristicsActionTypes.SET_QUALIFICATION_DESTINATION_FILTERS,
      data,
    };
  }

  static setQualificationDestination(data) {
    return {
      type: VisitorCharacteristicsActionTypes.SET_QUALIFICATION_DESTINATION,
      data,
    };
  }

  static setCatchmentAreasFilters(data) {
    return {
      type: VisitorCharacteristicsActionTypes.SET_QUALIFICATION_CATCHMENT_AREAS_FILTERS,
      data,
    };
  }

  static setCatchmentAreas(data) {
    return {
      type: VisitorCharacteristicsActionTypes.SET_QUALIFICATION_CATCHMENT_AREAS,
      data,
    };
  }

  static setLoading(statName) {
    return {
      type: VisitorCharacteristicsActionTypes.SET_LOADING,
      statName,
    };
  }

  static setAutoRefresh(autoRefresh) {
    return {
      type: VisitorCharacteristicsActionTypes.SET_AUTO_REFRESH,
      autoRefresh,
    };
  }

  static setInitialized(value) {
    return {
      type: VisitorCharacteristicsActionTypes.SET_INITIALIZED,
      value,
    };
  }

  static setFilters = (data) => {
    return {
      type: VisitorCharacteristicsActionTypes.SET_FILTERS,
      data,
    };
  };

  static setFilter = (key, value) => {
    return {
      type: VisitorCharacteristicsActionTypes.SET_FILTER,
      key,
      value,
    };
  };

  static retrieveQualificationIndicatorsByDayOfWeek = () => {
    return (dispatch, getState) => {
      if (
        !Abilities.ability.can("read", "visitorCharacteristics.visitorStats")
      ) {
        return null;
      }

      const selectedAreaId = get(
        getState().VisitorCharacteristics,
        "filters.selectedArea"
      );
      const selectedArea = VisitorCharacteristicsUtils.getSelectArea(
        get(getState().VisitorCharacteristics, "filters.areas"),
        selectedAreaId
      );
      const data = {
        from: get(getState().VisitorCharacteristics, "filters.from"),
        to: get(getState().VisitorCharacteristics, "filters.to"),
        area: selectedAreaId,
        dow: get(
          getState().VisitorCharacteristics,
          "qualificationIndicators.filters.dow"
        ),
        bounce_maximum_duration_in_minutes:
          selectedArea?.bounce_maximum_duration_in_minutes,
      };

      dispatch(
        VisitorCharacteristicsActioner.setLoading("qualificationIndicators")
      );

      return VisitorCharacteristicsApi.getVisitorStats(data)
        .then((data) => {
          dispatch(
            VisitorCharacteristicsActioner.setQualificationIndicators(data)
          );
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    };
  };

  static retrieveQualificationVisitorsByDayOfWeek = () => {
    return (dispatch, getState) => {
      if (
        !Abilities.ability.can(
          "read",
          "visitorCharacteristics.visitorDistribution"
        )
      ) {
        return null;
      }

      dispatch(
        VisitorCharacteristicsActioner.setLoading("qualificationVisitors")
      );

      const selectedAreaId = get(
        getState().VisitorCharacteristics,
        "filters.selectedArea"
      );
      const data = {
        from: get(getState().VisitorCharacteristics, "filters.from"),
        to: get(getState().VisitorCharacteristics, "filters.to"),
        area: selectedAreaId,
        dow: get(
          getState().VisitorCharacteristics,
          "qualificationVisitors.filters.dow"
        ),
      };

      const promises = [
        VisitorCharacteristicsApi.getVisitors(data),
        VisitorCharacteristicsApi.getCategorizeVisitors(data),
      ];
      return Promise.all(promises)
        .then(([visitorStats, categorizedVisitors]) => {
          dispatch(
            VisitorCharacteristicsActioner.setQualificationVisitors({
              ...visitorStats,
              ...categorizedVisitors,
            })
          );
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    };
  };

  static retrieveQualificationTimeByDayOfWeek = () => {
    return (dispatch, getState) => {
      if (
        !Abilities.ability.can(
          "read",
          "visitorCharacteristics.parkingTimeDistribution"
        )
      ) {
        return null;
      }

      dispatch(VisitorCharacteristicsActioner.setLoading("qualificationTime"));

      const selectedAreaId = get(
        getState().VisitorCharacteristics,
        "filters.selectedArea"
      );
      const maximumDurationInMinutes = get(
        getState().VisitorCharacteristics,
        "filters.averageTimeMaximumDuration"
      );

      const data = {
        from: get(getState().VisitorCharacteristics, "filters.from"),
        to: get(getState().VisitorCharacteristics, "filters.to"),
        area: selectedAreaId,
        dow: get(
          getState().VisitorCharacteristics,
          "qualificationTime.filters.dow"
        ),
        ...(maximumDurationInMinutes
          ? { maximum_duration_in_minutes: maximumDurationInMinutes }
          : {}),
      };

      return VisitorCharacteristicsApi.getTimeSpent(data)
        .then((timeSpentData) => {
          dispatch(
            VisitorCharacteristicsActioner.setQualificationTime(timeSpentData)
          );
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    };
  };

  static retrieveQualificationDestinationByDayOfWeek = () => {
    return (dispatch, getState) => {
      if (
        !Abilities.ability.can(
          "read",
          "visitorCharacteristics.destinationsDistribution"
        )
      ) {
        return null;
      }

      dispatch(
        VisitorCharacteristicsActioner.setLoading("qualificationDestination")
      );

      const areasId = get(
        getState().VisitorCharacteristics,
        "filters.areas"
      ).map((item) => item.id || item.area_id);

      const data = {
        from: get(getState().VisitorCharacteristics, "filters.from"),
        to: get(getState().VisitorCharacteristics, "filters.to"),
        areas: areasId,
        dow: get(
          getState().VisitorCharacteristics,
          "qualificationDestination.filters.dow"
        ),
        destination_minimum_duration_in_minutes: get(
          getState().VisitorCharacteristics,
          "filters.destinationsDistribution",
          10
        ),
      };

      return VisitorCharacteristicsApi.getDestinations(data)
        .then((destinationData) => {
          dispatch(
            VisitorCharacteristicsActioner.setQualificationDestination(
              destinationData
            )
          );
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    };
  };

  static retrieveCatchmentAreas = () => {
    return (dispatch, getState) => {
      if (
        !Abilities.ability.can(
          "read",
          "visitorCharacteristics.mapAppDistribution"
        ) &&
        !Abilities.ability.can(
          "read",
          "visitorCharacteristics.fullMapAppDistribution"
        )
      ) {
        return null;
      }

      dispatch(VisitorCharacteristicsActioner.setLoading("catchmentAreas"));
      const data = {
        from: get(getState().VisitorCharacteristics, "filters.from"),
        to: get(getState().VisitorCharacteristics, "filters.to"),
        contract: get(getState().Contract, "contract.id"),
        app_ids: get(getState().Map, "config.visitor_characteristics.app_ids"),
      };

      return VisitorCharacteristicsApi.getCatchmentAreas(data)
        .then((catchmentAreasData) => {
          dispatch(
            VisitorCharacteristicsActioner.setCatchmentAreas(catchmentAreasData)
          );
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    };
  };

  static retrieveAllStats = () => {
    return (dispatch, getState) => {
      dispatch(VisitorCharacteristicsActioner.setInitialized(false));
      const filters = getState().VisitorCharacteristics.filters;

      const promises = [
        dispatch(VisitorCharacteristicsActioner.retrieveCatchmentAreas()),
      ];

      if (filters?.selectedArea?.length > 0) {
        promises.push(
          ...[
            dispatch(
              VisitorCharacteristicsActioner.retrieveQualificationIndicatorsByDayOfWeek()
            ),
            dispatch(
              VisitorCharacteristicsActioner.retrieveQualificationVisitorsByDayOfWeek()
            ),
            dispatch(
              VisitorCharacteristicsActioner.retrieveQualificationTimeByDayOfWeek()
            ),
            dispatch(
              VisitorCharacteristicsActioner.retrieveQualificationDestinationByDayOfWeek()
            ),
          ]
        );
      }

      return Promise.all(promises).then((res) => {
        dispatch(VisitorCharacteristicsActioner.setInitialized(true));
        return res;
      });
    };
  };

  static retrieveLayersInfos() {
    return (dispatch, getState) => {
      const contractId = getState().Contract.contract.id;

      const LayersInfosPromises = [
        MapApi.getAreasInfos({
          contract_id: contractId,
          types: ["QUALIFICATIONANDFLOW"],
        }),
      ];

      return Promise.all(LayersInfosPromises).then(([areasInfos]) => {
        const mapVisitorCharacteristicsConfig = get(
          getState(),
          "Map.config.visitorCharacteristics",
          {}
        );
        const currentSelectedArea =
          get(getState(), "VisitorCharacteristics.filters.selectedArea") ||
          get(getState(), "VisitorCharacteristics.filters.areas", []).find(
            (area) => area.is_default
          )?.area_id;

        const formattedInfos =
          VisitorCharacteristicsUtils.formatLayersInfosFromApi(
            { ...areasInfos },
            {
              ...mapVisitorCharacteristicsConfig,
              areas: get(
                getState(),
                "VisitorCharacteristics.filters.areas",
                []
              ),
            },
            currentSelectedArea
          );

        dispatch(
          VisitorCharacteristicsActioner.setFilter(
            "areas",
            get(formattedInfos, "areas", []).sort((a, b) => {
              if (a.long_name < b.long_name) {
                return -1;
              }
              if (a.long_name > b.long_name) {
                return 1;
              }
              return 0;
            })
          )
        );
        dispatch(
          VisitorCharacteristicsActioner.setFilter(
            "selectedArea",
            get(formattedInfos, "selectedArea", [])
          )
        );
        setTimeout(() => {
          dispatch(VisitorCharacteristicsActioner.retrieveAllStats());
        }, 200);
        return formattedInfos;
      });
    };
  }
}

export default VisitorCharacteristicsActioner;
